/**
 * Favorites and history, on one page with two tabs.
 *
 * They are separate collections server-side for good reasons, but they answer the same
 * user question — "the things I care about" — and splitting them across two nav entries
 * would put two half-empty pages where one useful one belongs.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { BrowsableType, FavoriteDto, HistoryEntryDto, Paginated } from '@filmodasi/shared';
import { useApi } from '../api/hooks';
import { api } from '../api/client';
import { ItemCard, Spinner, ErrorBox, Empty } from '../components/ui';
import { useConfirm } from '../components/Dialog';

const LIBRARY_LABEL: Record<BrowsableType, string> = {
  live: 'Canlı',
  movie: 'Film',
  series: 'Dizi',
};

function Favorites() {
  const [type, setType] = useState<BrowsableType | ''>('');
  const favorites = useApi<FavoriteDto[]>(`/api/favorites${type ? `?type=${type}` : ''}`);

  if (favorites.loading && !favorites.data) return <Spinner />;
  if (favorites.error) return <ErrorBox message={favorites.error} onRetry={favorites.reload} />;

  return (
    <>
      <nav className="rail" aria-label="Tür">
        <button type="button" className={type === '' ? 'active' : ''} onClick={() => setType('')}>
          Hepsi
        </button>
        {(['live', 'movie', 'series'] as const).map((t) => (
          <button
            key={t}
            type="button"
            className={type === t ? 'active' : ''}
            onClick={() => setType(t)}
          >
            {LIBRARY_LABEL[t]}
          </button>
        ))}
      </nav>

      {favorites.data && favorites.data.length === 0 ? (
        <Empty title="Listeniz boş" action={<Link to="/movies">Filmlere göz at</Link>}>
          Bir film veya dizi sayfasındaki kalbe bastığınızda burada birikir.
        </Empty>
      ) : (
        <div className="grid">
          {favorites.data?.map((fav) => (
            <ItemCard key={fav.id} item={fav.item} />
          ))}
        </div>
      )}
    </>
  );
}

function History() {
  const history = useApi<Paginated<HistoryEntryDto>>('/api/history?limit=50');
  const [clearing, setClearing] = useState(false);
  const { confirm, dialog } = useConfirm();

  if (history.loading && !history.data) return <Spinner />;
  if (history.error) return <ErrorBox message={history.error} onRetry={history.reload} />;

  const clearAll = async () => {
    const ok = await confirm({
      title: 'İzleme geçmişi silinsin mi?',
      body: 'Bu evdeki bu profilin tüm geçmişi kaldırılır. Öneriler bir süre daha zayıf çalışabilir.',
      confirmLabel: 'Sil',
      danger: true,
    });
    if (!ok) return;
    setClearing(true);
    try {
      await api('/api/history', { method: 'DELETE' });
      history.reload();
    } finally {
      setClearing(false);
    }
  };

  const removeOne = async (id: string) => {
    await api(`/api/history/${id}`, { method: 'DELETE' }).catch(() => {});
    history.reload();
  };

  if (!history.data || history.data.total === 0) {
    return <Empty>Geçmiş boş.</Empty>;
  }

  return (
    <>
      <div className="mylist__actions">
        <button type="button" className="link" onClick={() => void clearAll()} disabled={clearing}>
          Geçmişi temizle
        </button>
      </div>

      <ul className="history">
        {history.data.items.map((entry) => (
          <li key={entry.id} className="history__row">
            <div className="history__main">
              {/* An entry whose item is gone is still shown — that is exactly when
                  someone wants to look up what a title was called. It simply is not a
                  link, because there is nothing to open. */}
              {entry.item ? (
                <Link to={entry.item.type === 'series' ? `/series/${entry.item.id}` : `/watch/${entry.item.id}`}>
                  {entry.title}
                </Link>
              ) : (
                <span className="history__gone">{entry.title}</span>
              )}
              <span className="history__meta">
                {LIBRARY_LABEL[entry.type]} ·{' '}
                {new Date(entry.startedAt).toLocaleString('tr-TR', {
                  day: 'numeric',
                  month: 'short',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
                {entry.secondsWatched > 0 && ` · ${Math.round(entry.secondsWatched / 60)} dk`}
              </span>
            </div>
            <button
              type="button"
              className="link"
              onClick={() => void removeOne(entry.id)}
              aria-label={`${entry.title} kaydını sil`}
            >
              Sil
            </button>
          </li>
        ))}
      </ul>

      {dialog}
    </>
  );
}

export function MyListPage() {
  const [tab, setTab] = useState<'favorites' | 'history'>('favorites');

  return (
    <main className="mylist">
      <header className="library__header">
        <h1>Listem</h1>
        <nav className="tabs">
          <button
            type="button"
            className={tab === 'favorites' ? 'active' : ''}
            onClick={() => setTab('favorites')}
          >
            Favoriler
          </button>
          <button
            type="button"
            className={tab === 'history' ? 'active' : ''}
            onClick={() => setTab('history')}
          >
            Geçmiş
          </button>
        </nav>
      </header>

      {tab === 'favorites' ? <Favorites /> : <History />}
    </main>
  );
}
